import React from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon } from '../../icons';
import type { Sex } from './AddChildStep1';

export interface Step2Props {
  header: React.ReactNode;
  firstName: string;
  lastName: string;
  sex: Sex;
  initials: string;
  age: number | null;
  birthDateFormatted: string;
  submitting: boolean;
  onBack: () => void;
  onSubmit: () => void;
}

export function Step2({ header, firstName, lastName, sex, initials, age, birthDateFormatted, submitting, onBack, onSubmit }: Step2Props) {
  const { t } = useTranslation();
  const isMale = sex === 'MALE';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {header}

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, py: 2 }}>
        <Box
          sx={(theme) => ({
            width: 88,
            height: 88,
            borderRadius: '50%',
            backgroundColor: isMale ? theme.app.infoSurface : theme.app.destructiveSurface,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          })}
        >
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {initials}
          </Typography>
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {firstName} {lastName}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {t('addChild.step2.sexLabel')}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {isMale ? t('addChild.step1.boy') : t('addChild.step1.girl')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {t('addChild.step2.ageLabel')}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {age !== null ? (
              <>
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {age} {t('children.years')}
                </Box>{' '}
                <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                  ({birthDateFormatted})
                </Box>
              </>
            ) : (
              birthDateFormatted
            )}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2.5 }}>
        <Button
          onClick={onBack}
          disabled={submitting}
          startIcon={<ArrowLeftIcon size={16} />}
          sx={(theme) => ({
            px: 5,
            py: 1.5,
            borderRadius: '100px',
            backgroundColor: theme.app.secondary,
            color: theme.app.secondaryForeground,
            border: `1px solid ${theme.app.borderStrong}`,
            fontWeight: 600,
            fontSize: 14,
            textTransform: 'none',
            '&:hover': { backgroundColor: theme.app.border },
          })}
        >
          {t('addChild.step2.back')}
        </Button>
        <Button
          onClick={onSubmit}
          disabled={submitting}
          fullWidth
          sx={(theme) => ({
            py: 1.5,
            borderRadius: '100px',
            backgroundColor: theme.app.primary,
            color: theme.app.primaryForeground,
            fontWeight: 600,
            fontSize: 14,
            textTransform: 'none',
            '&:hover': { backgroundColor: theme.app.primaryHover },
            '&.Mui-disabled': { opacity: 0.6 },
          })}
        >
          {submitting ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : t('addChild.step2.submit')}
        </Button>
      </Box>
    </Box>
  );
}
